import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Tag, Folder, Trash2, Pin, Hash, AlertTriangle, Share2, Check, Copy } from 'lucide-react';
import { useStore } from '../../store';
import { notesApi } from '../../api/client';
import { SyncIndicator, Badge } from '../ui';
import { useAutoSave } from '../../hooks';
import RichEditor from './RichEditor';
import type { Note } from '../../types';

const C = {
  amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623', amber400:'#C4811A', amber500:'#8B5A0A',
  ink:'#1A1814', inkLight:'#3D3A34', muted:'#7A7570', border:'#E8E4DC', surface:'#FAFAF8', white:'#FFFFFF', red:'#DC2626',
};
const font = { head:"'Sora',sans-serif", body:"'DM Sans',sans-serif" };

// ─── Portal Modal (same pattern as NoteCard) ──────────────────────────────────
const ConfirmPortal: React.FC<{
  title: string; message: string; confirmLabel: string;
  onConfirm: () => void; onCancel: () => void;
}> = ({ title, message, confirmLabel, onConfirm, onCancel }) => {
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  return ReactDOM.createPortal(
    <div onClick={stop} onMouseDown={stop} style={{ position:'fixed', inset:0, zIndex:99999 }}>
      <div onClick={(e) => { stop(e); onCancel(); }}
        style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.55)', backdropFilter:'blur(4px)' }}/>
      <div onClick={stop} style={{ position:'absolute', top:'50%', left:'50%', transform:'translate(-50%,-50%)', width:'min(90vw,400px)', background:C.white, borderRadius:20, padding:'28px 28px 22px', boxShadow:'0 24px 64px rgba(0,0,0,0.25)', zIndex:1 }}>
        <button onClick={onCancel} style={{ position:'absolute', top:14, right:14, background:'none', border:'none', cursor:'pointer', color:C.muted, padding:4, display:'flex' }}>
          <X size={16}/>
        </button>
        <div style={{ width:50, height:50, borderRadius:14, background:'#FEF2F2', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:16 }}>
          <AlertTriangle size={24} color={C.red}/>
        </div>
        <h2 style={{ fontFamily:font.head, fontWeight:800, fontSize:19, color:C.ink, marginBottom:8, letterSpacing:'-0.03em' }}>{title}</h2>
        <p style={{ fontSize:14, color:C.muted, lineHeight:1.75, fontFamily:font.body, marginBottom:24 }}
          dangerouslySetInnerHTML={{ __html: message }}/>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={onCancel} style={{ flex:1, height:44, borderRadius:10, border:`1.5px solid ${C.border}`, background:C.surface, cursor:'pointer', fontFamily:font.body, fontWeight:600, fontSize:14, color:'#3D3A34' }}>
            Cancel
          </button>
          <button onClick={onConfirm} style={{ flex:1, height:44, borderRadius:10, border:'none', background:'linear-gradient(135deg,#EF4444,#DC2626)', cursor:'pointer', fontFamily:font.body, fontWeight:700, fontSize:14, color:C.white, boxShadow:'0 4px 14px #DC262640' }}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Note Editor ──────────────────────────────────────────────────────────────
const NoteEditor: React.FC = () => {
  const openNoteId  = useStore(s => s.openNoteId);
  const notes       = useStore(s => s.notes);
  const setOpenNote = useStore(s => s.setOpenNote);
  const updateNote  = useStore(s => s.updateNote);
  const deleteNote  = useStore(s => s.deleteNote);
  const pinNote     = useStore(s => s.pinNote);
  const syncStatus  = useStore(s => s.syncStatus);
  const folders     = useStore(s => s.folders);

  const note: Note | undefined = notes.find(n => n.id === openNoteId);

  const [title,       setTitle]    = useState(note?.title || '');
  const [body,        setBody]     = useState(note?.body || '');
  const [plainText,   setPlain]    = useState(note?.plainText || '');
  const [tagInput,    setTagInput] = useState('');
  const [showTag,     setShowTag]  = useState(false);
  const [showDelete,  setShowDel]  = useState(false);  // delete modal
  const [showClose,   setShowClose]= useState(false);  // close modal (unsaved warning)
  const [shareUrl,    setShareUrl]  = useState<string | null>(note?.isPublic ? `${window.location.origin}/note/${note?.id}/share?token=${note?.publicToken}` : null);
  const [sharing,     setSharing]   = useState(false); // eslint-disable-line
  const [copied,      setCopied]    = useState(false);
  const titleRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setTitle(note?.title || '');
    setBody(note?.body || '');
    setPlain(note?.plainText || '');
  }, [note?.id]); // eslint-disable-line

  useEffect(() => {
    if (titleRef.current) {
      titleRef.current.style.height = 'auto';
      titleRef.current.style.height = titleRef.current.scrollHeight + 'px';
    }
  }, [title]);

  useAutoSave(note?.id || null, { title, body, plainText });

  const addTag = () => {
    if (!tagInput.trim() || !note) return;
    const tag = tagInput.trim().replace(/^#/, '');
    if (!note.tags.includes(tag)) updateNote(note.id, { tags: [...note.tags, tag] });
    setTagInput(''); setShowTag(false);
  };

  const removeTag = (tag: string) => {
    if (!note) return;
    updateNote(note.id, { tags: note.tags.filter(t => t !== tag) });
  };

  const handleShare = async () => {
    if (!note) return;
    setSharing(true);
    try {
      if (shareUrl) {
        // Revoke sharing
        await notesApi.unshare(note.id);
        setShareUrl(null);
      } else {
        // Generate share link
        const res = await notesApi.share(note.id);
        setShareUrl(res.shareUrl);
      }
    } catch (e) { console.error(e); }
    setSharing(false);
  };

  const copyShareUrl = () => {
    if (!shareUrl) return;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const folder = folders.find(f => f.id === note?.folderId);

  if (!note) return null;

  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', background:C.white, height:'100vh', overflow:'hidden' }}>

      {/* ── Toolbar ────────────────────────────────────────────────────────── */}
      <div style={{ height:52, borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:4, padding:'0 16px', flexShrink:0 }}>
        {folder && <Badge color={folder.color}><Folder size={10} style={{ display:'inline', marginRight:3 }}/>{folder.name}</Badge>}

        {/* Push buttons to right */}
        <div style={{ flex:1 }}/>

        <SyncIndicator status={syncStatus}/>
        <div style={{ width:1, height:16, background:C.border, margin:'0 4px' }}/>

        {/* Pin */}
        <ToolBtn onClick={() => pinNote(note.id, !note.isPinned)} title={note.isPinned ? 'Unpin' : 'Pin'} active={note.isPinned}>
          <Pin size={15} fill={note.isPinned ? C.amber400 : 'none'}/>
        </ToolBtn>

        {/* Tag */}
        <ToolBtn onClick={() => setShowTag(s => !s)} title="Add tag">
          <Tag size={15}/>
        </ToolBtn>

        {/* Share */}
        <ToolBtn onClick={handleShare} title={shareUrl ? 'Sharing on — click to stop' : 'Share note'} active={!!shareUrl}>
          <Share2 size={15}/>
        </ToolBtn>

        {/* Delete — opens modal */}
        <ToolBtn onClick={() => setShowDel(true)} title="Delete note" danger>
          <Trash2 size={15}/>
        </ToolBtn>

        {/* Close */}
        <ToolBtn onClick={() => setOpenNote(null)} title="Close">
          <X size={16}/>
        </ToolBtn>
      </div>

      {/* ── Title + Tags ────────────────────────────────────────────────────── */}
      <div style={{ padding:'24px 32px 0' }}>
        <textarea
          ref={titleRef} value={title} onChange={e => setTitle(e.target.value)}
          placeholder="Note title" rows={1}
          style={{ width:'100%', resize:'none', overflow:'hidden', border:'none', outline:'none', fontFamily:font.head, fontWeight:800, fontSize:'clamp(20px,2.5vw,30px)', color:C.ink, background:'transparent', letterSpacing:'-0.03em', lineHeight:1.2 }}
        />
        <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginTop:10, alignItems:'center' }}>
          {note.tags.map(t => (
            <div key={t} style={{ display:'flex', alignItems:'center', gap:4, padding:'3px 9px', borderRadius:20, background:C.amber100, border:`1px solid ${C.amber200}` }}>
              <Hash size={10} color={C.amber400}/>
              <span style={{ fontSize:11, fontWeight:700, color:C.amber500, fontFamily:font.body }}>{t}</span>
              <button onClick={() => removeTag(t)} style={{ background:'none', border:'none', cursor:'pointer', color:C.amber400, padding:0, display:'flex', marginLeft:2 }}>
                <X size={9}/>
              </button>
            </div>
          ))}
          {showTag && (
            <input autoFocus value={tagInput} onChange={e => setTagInput(e.target.value)}
              onKeyDown={e => { if (e.key==='Enter') { e.preventDefault(); addTag(); } if (e.key==='Escape') setShowTag(false); }}
              onBlur={addTag} placeholder="tag name"
              style={{ height:26, padding:'0 10px', borderRadius:20, border:`1.5px solid ${C.amber300}`, fontFamily:font.body, fontSize:11, outline:'none', background:C.amber100, width:100, color:C.amber500 }}/>
          )}
          <button onClick={() => setShowTag(s => !s)}
            style={{ display:'flex', alignItems:'center', gap:4, padding:'3px 8px', borderRadius:20, border:`1px dashed ${C.border}`, background:'none', cursor:'pointer', color:C.muted, fontSize:11, fontFamily:font.body }}>
            <Hash size={10}/> Add tag
          </button>
        </div>
        <div style={{ height:1, background:C.border, marginTop:16 }}/>
        {shareUrl && (
          <div style={{ margin:'12px 0 0', padding:'10px 14px', background:C.amber100, border:`1px solid ${C.amber200}`, borderRadius:10, display:'flex', alignItems:'center', gap:10, flexWrap:'wrap' }}>
            <Share2 size={13} color={C.amber400}/>
            <span style={{ fontSize:12, color:C.amber500, fontFamily:font.body, fontWeight:600, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {shareUrl}
            </span>
            <button onClick={copyShareUrl} style={{ display:'flex', alignItems:'center', gap:5, padding:'4px 10px', borderRadius:7, border:`1px solid ${C.amber300}`, background:'none', cursor:'pointer', fontSize:12, fontFamily:font.body, fontWeight:600, color:C.amber500, flexShrink:0 }}>
              {copied ? <><Check size={11}/> Copied!</> : <><Copy size={11}/> Copy link</>}
            </button>
          </div>
        )}
      </div>

      {/* ── Rich editor ─────────────────────────────────────────────────────── */}
      <RichEditor content={body} onChange={(html, text) => { setBody(html); setPlain(text); }} placeholder="Start writing…"/>

      {/* ── Footer ──────────────────────────────────────────────────────────── */}
      <div style={{ padding:'8px 32px', borderTop:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', flexShrink:0 }}>
        <span style={{ fontSize:11, color:C.muted, fontFamily:font.body }}>
          {plainText.trim().split(/\s+/).filter(Boolean).length} words
        </span>
        <span style={{ fontSize:11, color:C.muted, fontFamily:font.body }}>
          {new Date(note.updatedAt).toLocaleString('en-GB', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}
        </span>
      </div>

      {/* ── Delete confirmation modal ────────────────────────────────────────── */}
      {showDelete && (
        <ConfirmPortal
          title="Delete this note?"
          message="This is <strong style='color:#1A1814'>permanent and cannot be undone.</strong> The note and all its content will be deleted forever."
          confirmLabel="Yes, delete forever"
          onConfirm={() => { setShowDel(false); deleteNote(note.id); setOpenNote(null); }}
          onCancel={() => setShowDel(false)}
        />
      )}

      {/* ── Close confirmation (only if note has content) ────────────────────── */}
      {showClose && (
        <ConfirmPortal
          title="Close this note?"
          message="Your changes are auto-saved. Closing will return you to the note list."
          confirmLabel="Close note"
          onConfirm={() => { setShowClose(false); setOpenNote(null); }}
          onCancel={() => setShowClose(false)}
        />
      )}
    </div>
  );
};

// ─── Toolbar button ───────────────────────────────────────────────────────────
const ToolBtn: React.FC<{
  onClick: () => void; title: string;
  children: React.ReactNode; active?: boolean; danger?: boolean;
}> = ({ onClick, title, children, active, danger }) => (
  <button
    onClick={onClick} title={title}
    style={{ background:'none', border:'none', cursor:'pointer', padding:6, display:'flex', borderRadius:7, color: active ? '#C4811A' : danger ? '#DC2626' : '#7A7570', transition:'background .12s' }}
    onMouseEnter={e => (e.currentTarget as HTMLButtonElement).style.background = danger ? '#FEF2F2' : '#F5F5F3'}
    onMouseLeave={e => (e.currentTarget as HTMLButtonElement).style.background = 'none'}
  >
    {children}
  </button>
);

export default NoteEditor;